import { Component, Input, OnInit } from '@angular/core';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { Transaction } from 'src/app/shared/models/transaction/transaction';
import { TransactionType } from 'src/app/shared/models/transaction/transaction-type';

@Component({
  selector: 'app-transaction-card',
  templateUrl: './transaction-card.component.html',
  styleUrls: ['./transaction-card.component.scss'],
})
/**
 * Transaction Card for points balance screen
 */
export class TransactionCardComponent implements OnInit {
  @Input() quantity!: number;
  @Input() transactionType!: TransactionType;
  @Input() user!: string;
  @Input() generationDate!: Date;
  @Input() expirationDate?: Date | null;
  @Input() transactions!: Transaction[];
  show = false;
  loadingTransaction = false;
  transactionLabels = {
    onLabel: 'on',
    expiredLabel: 'expired-on',
    transactionError: 'transaction-error',
    portPrinters: 'port-printers',
    newPrinterGift: 'new-printer-gift',
    addedBy: 'added-by',
    pointsBy: 'points-by',
    expiresOn: 'expires-on',
    expiresFrom: 'expires-from',
    add: 'transaction-type-add',
    spend: 'transaction-type-spend',
    expire: 'transaction-type-expire',
    remove: 'transaction-type-remove',
    port: 'transaction-type-port',
    refund: 'transaction-type-refund',
    new_equipment: 'transaction-type-new-equipment',
  };
  codesLabel: string = '';
  errorExpanded? = false;
  transactionTypes = TransactionType;

  language = '';
  transactionClass!: string;
  dateFormat?: string;

  /**
   * Constructor
   */
  constructor(private translationService: TranslationService) {}

  /**
   * on init
   */
  async ngOnInit(): Promise<void> {
    this.translationService.translateObject(this.transactionLabels);
    this.language = this.translationService.getActiveLang();
    this.dateFormat = this.getDateFormatForLocale(this.language);
    this.transactionClass = this.classText(this.transactionType);

    if (this.transactionClass == 'new-equipment') {
      this.user = await this.translationService.translateString(
        this.transactionLabels.newPrinterGift
      );
    }
  }

  /**
   * Returns the footer prefix text (without date),
   * e.g. "Welcome Points by System", "Points by Admin", etc.,
   * based on the transaction type and transaction user type.
   *
   * @return {string}
   */
  get footerPrefix(): string {
    if (!this.transactions || !this.transactions.length) {
      return '';
    }
    const tx = this.transactions[0];
    if (this.transactionType === this.transactionTypes.PORT) {
      return 'Monthly PORT points ';
    }

    switch (tx.transactionType) {
      case this.transactionTypes.SPEND:
        return 'Points spent';

      case this.transactionTypes.EXPIRE:
        return 'Points expired';

      case this.transactionTypes.REMOVE:
        return 'Points removed';

      case this.transactionTypes.REFUND:
        return 'Refunded Points';
    }

    switch (tx.transactionUserType) {
      case 'SYSTEM':
        return 'Welcome Points by System';

      case 'ADMIN':
      case 'SYSADMIN':
        return 'Points granted by Admin';

      case 'REPRESENTATIVE':
      default:
        return 'Points entered by user';
    }
  }

  /**
   * Toggles the accordion expansion
   */
  toggleShow() {
    this.show = !this.show;
    if (this.show && !this.loadingTransaction) {
      this.pullTransactionsDetail();
    }
  }

  /**
   * Makes request to get transaction detail and controls loading
   */
  pullTransactionsDetail() {
    this.errorExpanded = false;
  }

  /**
   * Returns the symbol depending on type of the transaction
   *  @param {TransactionType} transaction
   *  @return {string};
   */
  returnTransactionType(transaction: TransactionType): string {
    return transaction === TransactionType.SPEND ||
      transaction === TransactionType.EXPIRE ||
      transaction === TransactionType.REMOVE
      ? '-'
      : '+';
  }

  /**
   * Returns the icon name depending on type of the transaction
   *  @param {TransactionType} transaction
   *  @return {string};
   */
  returnIconType(transaction: TransactionType): string {
    return transaction === TransactionType.SPEND ||
      transaction === TransactionType.EXPIRE ||
      transaction === TransactionType.REMOVE
      ? 'hourglass_bottom'
      : 'add_task';
  }

  /**
   * Returns the format name depending on type of the transaction
   *  @param {TransactionType} type
   *  @return {string};
   */
  formatTransactionTypeName(type: TransactionType | string): string {
    if (!type) return '';
    const labelKey = type.toLowerCase() as keyof typeof this.transactionLabels;
    const translatedName = this.transactionLabels[labelKey];
    return (
      translatedName ||
      type
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }

  /**
   * Returns true if transaction is spend
   *  @param {TransactionType} transaction
   *  @return {boolean};
   */
  isExpireTransaction(transaction: TransactionType): boolean {
    return transaction === TransactionType.EXPIRE;
  }

  /**
   * Returns true if transaction is add
   *  @param {TransactionType} transaction
   *  @return {boolean};
   */
  isAddTransaction(transaction: TransactionType): boolean {
    return (
      transaction === TransactionType.ADD ||
      transaction === TransactionType.NEW_EQUIPMENT
    );
  }

  /**
   * Returns class for text
   *  @param {TransactionType} transaction
   *  @return {string};
   */
  classText(transaction: TransactionType): string {
    return transaction
      ? transaction.toLocaleLowerCase().replace(/_/g, '-')
      : '';
  }

  /**
   * Return format date using dots instead of slash based on language
   * @param {string} locale
   * @return {string}
   */
  private getDateFormatForLocale(locale: string): string {
    if (locale === 'en') {
      return 'MM.dd.yyyy';
    }
    return 'dd.MM.yyyy';
  }
}
